"use client";

import {
  Navbar,
  NavbarBrand,
  NavbarContent,
  NavbarItem,
  Button,
  NavbarMenu,
  NavbarMenuItem,
} from "@nextui-org/react";
import Link from "next/link";
import { navItems } from "@/config/statics";
import SearchIcon from "@/helpers/svg/searchIcon.svg";
import ManabakhBrandIcon from "@/helpers/svg/manabakhBrandIcon.svg";
import { useEffect, useState } from "react";
import LeftArrowIcon from "@/helpers/svg/leftArrowIcon.svg";
import { ROUTES } from "@/routes/appRoutes";
import HamburgerMenu from "@/helpers/svg/hamburgerMenu.svg";
import MobileUserIcon from "@/helpers/svg/mobileUserIcon.svg";
import MobileSearchIcon from "@/helpers/svg/mobileSearchIcon.svg";
import TabletSearchIcon from "@/helpers/svg/tabletSearchIcon.svg";
import TabletUserIcon from "@/helpers/svg/tabletUserIcon.svg";
import { useAppSelector } from "@/redux/hooks";
import HomeManabakhLogo from "@/helpers/svg/homeManabakhLogo.svg";
import CloseMenuIcon from "@/helpers/svg/closeMenuIcon.svg";
import classNames from "classnames";
import { isBrowser } from "@/helpers/browser.utils";

import UserDropdown from "./UserDropdown";

export default function HeaderNavbar() {
  const state = useAppSelector((state) => state.Auth);
  const [openMenu, setOpenMenu] = useState(false);
  const [isScrolledDown, setIsScrolledDown] = useState(false);

  useEffect(() => {
    if (!isBrowser()) return;

    // Function to handle scroll events
    const handleScroll = () => {
      // Get the current scroll position
      const scrollY = window.scrollY || document.documentElement.scrollTop;

      // Update the state based on scroll position
      if (scrollY > 50) {
        setIsScrolledDown(true);
      } else {
        setIsScrolledDown(false);
      }
    };

    // Attach the scroll event listener
    window.addEventListener("scroll", handleScroll);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Navbar
      maxWidth="full"
      className={classNames(
        "fixed font-semibold",
        openMenu && "!bg-[rgba(18,17,17,0.95)]",
      )}
      position="sticky"
      isBlurred={false}
      isMenuOpen={openMenu}
      style={{
        background: "transparent",
        zIndex: 999,
      }}
      onMenuOpenChange={(e) => {
        setOpenMenu(e);
      }}
    >
      <div
        className="h-[4rem] lg:h-[4.5rem]"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          background: isScrolledDown
            ? "rgba(10, 10, 10, 0.7)"
            : "rgba(10, 10, 10, 0)",
          backdropFilter: isScrolledDown ? "blur(24px)" : "blur(0px)",
          WebkitBackdropFilter: isScrolledDown ? "blur(24px)" : "blur(0px)",
          borderBottom: isScrolledDown
            ? "1px solid rgba(255, 255, 255, 0.08)"
            : "1px solid transparent",
          transition: "all 0.5s ease",
          opacity: openMenu ? 0 : 1,
          zIndex: 1,
        }}
      />
      <div className="md:container mx-auto flex w-full items-center md:mt-[7px]">
        <NavbarContent className="lg:mr-6 z-10" justify="start">
          <NavbarBrand>
            <div className="flex justify-center items-center font-bold text-inherit">
              <Link href={ROUTES.home}>
                <div className="hidden lg:flex ml-2">
                  <HomeManabakhLogo />
                </div>
              </Link>
              <div className="flex lg:hidden justify-center items-center ml-2">
                {openMenu ? (
                  <div onClick={() => setOpenMenu(!openMenu)}>
                    <CloseMenuIcon />
                  </div>
                ) : (
                  <div onClick={() => setOpenMenu(!openMenu)}>
                    <HamburgerMenu />
                  </div>
                )}
                <Link href={ROUTES.home}>
                  <div className="mr-2">
                    <ManabakhBrandIcon />
                  </div>
                </Link>
              </div>
            </div>
          </NavbarBrand>
        </NavbarContent>
        <NavbarContent
          className={"flex flex-col rounded-full z-10"}
          justify="center"
        >
          <div
            className={classNames(
              `rounded-full w-[640px] items-center hidden lg:flex lg:min-h-[66px]`,
            )}
            style={{
              background: "rgba(255, 255, 255, 0.06)",
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.15)",
            }}
          >
            <div
              className={classNames(
                "flex lg:gap-11 md:gap-7 transition-all overflow-hidden items-center duration-700",
                "w-[620px] my-5 mx-8 opacity-100",
              )}
            >
              {navItems?.map((item) => {
                return (
                  <NavbarItem key={item.name} className="gap-8">
                    <Link
                      prefetch={true}
                      href={item.href}
                      className="text-primary-white hover:text-[#d00e0e] transition-colors"
                      aria-current="page"
                    >
                      {item.value}
                    </Link>
                  </NavbarItem>
                );
              })}
            </div>
            <Link href={ROUTES.search}>
              <Button isIconOnly disableRipple variant="light" className="ml-5">
                <SearchIcon />
              </Button>
            </Link>
          </div>
        </NavbarContent>
        <NavbarContent
          justify="end"
          className={classNames(
            state?.profile?.isLogin && "!gap-1",
            "z-10 lg:ml-3",
          )}
        >
          {!state.profile.isLogin ? (
            <>
              <Link href={ROUTES.auth.login}>
                <Button
                  className="text-primary-white hidden lg:flex text-center font-semibold"
                  color="primary"
                  variant="light"
                  endContent={<LeftArrowIcon />}
                >
                  <span className="mb-1.5">ورود</span>
                </Button>
              </Link>
              <Link href={ROUTES.search} className="hidden sm:flex lg:hidden">
                <TabletSearchIcon />
              </Link>
              <div className="hidden sm:flex lg:hidden">
                <Link href={ROUTES.auth.login}>
                  <TabletUserIcon />
                </Link>
              </div>
              <Link href={ROUTES.search} className="flex sm:hidden">
                <MobileSearchIcon />
              </Link>
              <div className="flex sm:hidden">
                <Link href={ROUTES.auth.login}>
                  <MobileUserIcon />
                </Link>
              </div>
            </>
          ) : (
            <>
              <Link href={ROUTES.search} className="hidden sm:flex lg:hidden">
                <TabletSearchIcon />
              </Link>
              <Link href={ROUTES.search} className="flex sm:hidden">
                <MobileSearchIcon />
              </Link>
              <UserDropdown />
            </>
          )}
        </NavbarContent>
      </div>
      <NavbarMenu
        className={classNames(
          "flex flex-row justify-around pt-16 top-0 max-h-[132px]",
          openMenu && "!overflow-y-hidden",
        )}
        style={{
          background: "rgba(18, 17, 17, 0.95)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
        }}
        disableAnimation="false"
      >
        {navItems?.map((item) => (
          <NavbarMenuItem key={item.name}>
            <Link
              className="w-full text-[#F0F0F0] text-[13.2px] xs:text-[15.4px] sm:text-[17.6px] md:text-[19.8px]"
              href={item.href}
              size="lg"
              onClick={() => {
                setOpenMenu(false);
              }}
            >
              {item.value}
            </Link>
          </NavbarMenuItem>
        ))}
      </NavbarMenu>
    </Navbar>
  );
}
